import { brandApi, affiliateApi, agencyApi } from "../lib/axios";

// Types for FAQ
export interface FAQ {
  id: string;
  question: string;
  answer: string;
  priority?: number;
  targetAudience?: "brand" | "affiliate" | "agency";
  createdAt?: string;
  updatedAt?: string;
}

export interface FAQResponse {
  data: FAQ[];
  success: boolean;
  message?: string;
}

// Determine user type based on localStorage tokens
const getUserType = (): "brand" | "affiliate" | "agency" | null => {
  const brandToken = localStorage.getItem("brand_token");
  const affiliateToken = localStorage.getItem("token"); // affiliate uses "token" key
  const agencyToken = localStorage.getItem("agency_token");

  if (brandToken) return "brand";
  if (affiliateToken) return "affiliate";
  if (agencyToken) return "agency";

  return null;
};

// Get the appropriate axios instance based on user type
const getApiInstance = () => {
  const userType = getUserType();

  switch (userType) {
    case "brand":
      return brandApi;
    case "affiliate":
      return affiliateApi;
    case "agency":
      return agencyApi;
    default:
      throw new Error("No valid authentication token found");
  }
};

// FAQ Service
export const faqService = {
  // Get FAQs based on user type
  getFAQs: async (): Promise<FAQResponse> => {
    try {
      const apiInstance = getApiInstance();
      const userType = getUserType();

      // Make request with appropriate instance and user type context
      const response = await apiInstance.get(`/faq?userType=${userType}`);

      return {
        data: response.data.data || response.data,
        success: true,
        message: response.data.message,
      };
    } catch (error: any) {
      console.error("Error fetching FAQs:", error);
      return {
        data: [],
        success: false,
        message: error.response?.data?.message || "Failed to fetch FAQs",
      };
    }
  },

  // Get FAQ by ID
  getFAQById: async (id: string): Promise<FAQ | null> => {
    try {
      const apiInstance = getApiInstance();
      const response = await apiInstance.get(`/faq/${id}`);

      return response.data.data || response.data;
    } catch (error: any) {
      console.error("Error fetching FAQ by ID:", error);
      return null;
    }
  },

  // Get FAQs by category (optional)
  getFAQsByCategory: async (category: string): Promise<FAQResponse> => {
    try {
      const apiInstance = getApiInstance();
      const userType = getUserType();

      const response = await apiInstance.get(
        `/faq?category=${category}&userType=${userType}`
      );

      return {
        data: response.data.data || response.data,
        success: true,
        message: response.data.message,
      };
    } catch (error: any) {
      console.error("Error fetching FAQs by category:", error);
      return {
        data: [],
        success: false,
        message: error.response?.data?.message || "Failed to fetch FAQs",
      };
    }
  },

  // Search FAQs (optional)
  searchFAQs: async (query: string): Promise<FAQResponse> => {
    try {
      const apiInstance = getApiInstance();
      const userType = getUserType();

      const response = await apiInstance.get(
        `/faq/search?q=${encodeURIComponent(query)}&userType=${userType}`
      );

      return {
        data: response.data.data || response.data,
        success: true,
        message: response.data.message,
      };
    } catch (error: any) {
      console.error("Error searching FAQs:", error);
      return {
        data: [],
        success: false,
        message: error.response?.data?.message || "Failed to search FAQs",
      };
    }
  },

  // Utility function to get current user type
  getCurrentUserType: getUserType,
};

export default faqService;
